const { VITE_API_URL: API, VITE_AUTH_SCOPE: SCOPE } = import.meta.env;

const jsonHeaders: HeadersInit = {
  'Content-Type': 'application/json',
  'X-Auth-Scope': SCOPE,
};

const baseInit: RequestInit = {
  credentials: 'include',
  headers: jsonHeaders,
};

export interface PresignSingleResponse {
  url: string;
  key: string;
}

export interface CreateMultipartResponse {
  uploadId: string;
  key: string;
}

export interface SignPartResponse {
  url: string;
}

export interface CompleteMultipartResponse {
  location?: string;
}

export interface S3Part {
  PartNumber?: number;
  ETag?: string;
}

export interface S3RequestErrorContext {
  url: string;
  status: number;
  requestId: string | null;
  body?: string;
  uploadId?: string;
  key?: string;
  partNumber?: number;
}

export class S3RequestError extends Error {
  context: S3RequestErrorContext;

  constructor(message: string, context: S3RequestErrorContext) {
    super(message);
    this.name = 'S3RequestError';
    this.context = context;
  }
}

async function toErrorContext(
  res: Response,
  extra: Partial<
    Pick<S3RequestErrorContext, 'uploadId' | 'key' | 'partNumber'>
  > = {},
): Promise<S3RequestErrorContext> {
  let body: string | undefined;
  try {
    body = (await res.text()).slice(0, 500) || undefined;
  } catch {
    body = undefined;
  }
  return {
    url: res.url,
    status: res.status,
    requestId: res.headers.get('x-request-id'),
    body,
    ...extra,
  };
}

export async function presignSingle(
  filename: string,
  contentType: string,
): Promise<PresignSingleResponse> {
  const res = await fetch(`${API}/s3/presign`, {
    ...baseInit,
    method: 'POST',
    body: JSON.stringify({ filename, content_type: contentType }),
  });
  if (!res.ok) {
    throw new S3RequestError(
      `presign failed: ${res.status}`,
      await toErrorContext(res),
    );
  }
  return res.json();
}

export async function createMultipart(
  filename: string,
  contentType: string,
): Promise<CreateMultipartResponse> {
  const res = await fetch(`${API}/s3/multipart`, {
    ...baseInit,
    method: 'POST',
    body: JSON.stringify({ filename, content_type: contentType }),
  });
  if (!res.ok) {
    throw new S3RequestError(
      `createMultipart failed: ${res.status}`,
      await toErrorContext(res),
    );
  }
  const data = (await res.json()) as { upload_id: string; key: string };
  return { uploadId: data.upload_id, key: data.key };
}

export async function signPart(
  uploadId: string,
  key: string,
  partNumber: number,
): Promise<SignPartResponse> {
  const url = `${API}/s3/multipart/${encodeURIComponent(uploadId)}/presign/${partNumber}?key=${encodeURIComponent(key)}`;
  const res = await fetch(url, baseInit);
  if (!res.ok) {
    throw new S3RequestError(
      `signPart failed: ${res.status}`,
      await toErrorContext(res, { uploadId, key, partNumber }),
    );
  }
  return res.json();
}

export async function completeMultipart(
  uploadId: string,
  key: string,
  parts: S3Part[],
): Promise<CompleteMultipartResponse> {
  // Uppy types PartNumber/ETag as optional, but S3 rejects a completion that is
  // missing either on any part. Fail loudly here rather than POSTing nulls that
  // the backend would 422 on with a less obvious error.
  const normalizedParts = parts.map((p, index) => {
    if (p.PartNumber == null || p.ETag == null) {
      throw new Error(
        `completeMultipart: part #${index} is missing ${
          p.PartNumber == null ? 'PartNumber' : 'ETag'
        }`,
      );
    }
    return { part_number: p.PartNumber, etag: p.ETag };
  });
  const res = await fetch(
    `${API}/s3/multipart/${encodeURIComponent(uploadId)}/complete`,
    {
      ...baseInit,
      method: 'POST',
      body: JSON.stringify({ key, parts: normalizedParts }),
    },
  );
  if (!res.ok) {
    throw new S3RequestError(
      `completeMultipart failed: ${res.status}`,
      await toErrorContext(res, { uploadId, key }),
    );
  }
  return res.json();
}

export async function abortMultipart(
  uploadId: string,
  key: string,
): Promise<void> {
  await fetch(
    `${API}/s3/multipart/${encodeURIComponent(uploadId)}?key=${encodeURIComponent(key)}`,
    { ...baseInit, method: 'DELETE' },
  );
}
